import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import cogoToast from 'cogo-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { spotlights } from '@shopmy/universal';

import { getBrandFeatureFlags, setBrandFeatureFlags } from '../../api/spotlights';
import './BrandFeatureFlags.scss';

const { SPOTLIGHT_BRAND_FEATURE_FLAGS, getSpotlightBrandFeatureFlagPrerequisites, getSpotlightBrandFeatureFlagDependents } = spotlights;

export const featureFlagLabel = key => SPOTLIGHT_BRAND_FEATURE_FLAGS.find(flag => flag.key === key)?.label || key;

const BrandFeatureFlagsModal = ({ brand, onClose, onDone }) => {
  const [stored, setStored] = useState(null);
  const [draft, setDraft] = useState({});
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    getBrandFeatureFlags(brand.Brand_id)
      .then(r => {
        if (!active) return;
        setStored(r.featureFlags);
        setDraft(_.mapValues(r.featureFlags, value => !!value));
      })
      .catch(e => active && setLoadError(typeof e === 'string' ? e : 'Could not load feature flags.'));
    return () => {
      active = false;
    };
  }, [brand.Brand_id]);

  const toggle = (key, checked) =>
    setDraft(prev => {
      const next = { ...prev, [key]: checked };
      if (checked) getSpotlightBrandFeatureFlagPrerequisites(key).forEach(prerequisite => (next[prerequisite] = true));
      return next;
    });

  const changes = stored ? _.pickBy(draft, (value, key) => value !== !!stored[key]) : {};
  const changedCount = Object.keys(changes).length;

  const save = async () => {
    setSaving(true);
    try {
      await setBrandFeatureFlags(brand.Brand_id, changes);
      cogoToast.success(`Updated ${changedCount} feature flag${changedCount === 1 ? '' : 's'} for ${brand.brandName}`);
      onDone && onDone();
      onClose();
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to update feature flags.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='sl-modal-overlay' onMouseDown={() => !saving && onClose()}>
      <div className='sl-modal' onMouseDown={e => e.stopPropagation()}>
        <div className='sl-modal-head'>
          <div className='sl-modal-title'>Feature flags · {brand.brandName}</div>
          <button className='sl-modal-close' disabled={saving} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className='sl-modal-body'>
          {loadError ? (
            <div className='sl-error'>{loadError}</div>
          ) : !stored ? (
            <div className='sl-empty'>Loading…</div>
          ) : (
            <>
              <div className='sl-flag-list'>
                {SPOTLIGHT_BRAND_FEATURE_FLAGS.map(flag => {
                  const stranded = draft[flag.key] ? [] : getSpotlightBrandFeatureFlagDependents(flag.key).filter(dependent => draft[dependent]);
                  return (
                    <label className='sl-flag-row' key={flag.key}>
                      <div className='sl-flag-info'>
                        <span className='label'>
                          {flag.label}
                          {stored[flag.key] === null && <span className='sl-pill'>Unset</span>}
                          {flag.key in changes && <span className='sl-pill blue'>Changed</span>}
                        </span>
                        <span className='description'>{flag.description}</span>
                        {flag.requires.length > 0 && <span className='requires'>Needs {flag.requires.map(featureFlagLabel).join(' + ')}</span>}
                        {stranded.length > 0 && (
                          <span className='warning'>{stranded.map(featureFlagLabel).join(', ')} stop working without this</span>
                        )}
                      </div>
                      <input type='checkbox' checked={!!draft[flag.key]} onChange={e => toggle(flag.key, e.target.checked)} />
                    </label>
                  );
                })}
              </div>
              <div className='sl-flag-footer'>
                <button className='sl-btn secondary' onClick={onClose}>
                  Cancel
                </button>
                <button className='sl-btn' disabled={saving || !changedCount} onClick={save}>
                  {saving ? 'Saving…' : changedCount ? `Save ${changedCount} change${changedCount === 1 ? '' : 's'}` : 'Save'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrandFeatureFlagsModal;
